import { useCallback, useEffect, useRef } from "react";

import { useAuthContext } from "../../containers/AuthContextWrapper";
import {
  removeGroupTypingUser,
  setGroupTypingUser,
} from "../../lib/redisTyping";

const __DEV__ = process.env.NODE_ENV !== "production";

const SEND_THROTTLE_MS = 1500;
const STOP_DELAY_MS = 4000;

export function useTypingPublisher(groupChatId?: string) {
  const { session } = useAuthContext();
  const userId = session?.user?.id;

  const lastSentRef = useRef(0);
  const stopTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const stopTyping = useCallback(() => {
    if (!groupChatId || !userId) return;
    removeGroupTypingUser(groupChatId, userId).catch((error) => {
      if (__DEV__) {
        console.warn("Failed to clear typing state", error);
      }
    });
  }, [groupChatId, userId]);

  const scheduleStop = useCallback(() => {
    if (stopTimerRef.current) {
      clearTimeout(stopTimerRef.current);
    }
    stopTimerRef.current = setTimeout(() => {
      stopTyping();
      stopTimerRef.current = null;
    }, STOP_DELAY_MS);
  }, [stopTyping]);

  const notifyTyping = useCallback(() => {
    if (!groupChatId || !userId) return;
    const now = Date.now();
    if (now - lastSentRef.current > SEND_THROTTLE_MS) {
      setGroupTypingUser(groupChatId, userId).catch((error) => {
        if (__DEV__) {
          console.warn("Failed to update typing state", error);
        }
      });
      lastSentRef.current = now;
    }
    scheduleStop();
  }, [groupChatId, userId, scheduleStop]);

  useEffect(() => {
    return () => {
      if (stopTimerRef.current) {
        clearTimeout(stopTimerRef.current);
        stopTimerRef.current = null;
      }
      stopTyping();
    };
  }, [stopTyping]);

  return {
    notifyTyping,
    stopTyping,
  };
}
